import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  TextField,
  Button,
  FormControl,
  Box,
  MenuItem,
  Select,
  FormControlLabel,
  Checkbox,
  Typography,
  FormHelperText,
  Tooltip,
  InputAdornment,
  IconButton,
  SvgIconProps,
  Link,
} from '@mui/material';
import { ThemeProvider } from '@emotion/react';
import { darkTheme, theme } from 'theme/theme';
import './Formulario.scss';
import Icono from 'components/public/Icono/Icono';
import Alerta from '../Alerta.tsx/Alerta';
import PantallaExito from '../PantallaExito/PantallaExito';
import LoadingButton from '@mui/lab/LoadingButton';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { VisibilityOff, Visibility } from '@mui/icons-material';
import PhoneIcon from '@mui/icons-material/Phone';
import { IFormato, IFormulario, ISelectOptions } from 'utils/models/interfaces';
import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';
import { Divider, InputLabel } from '@mui/material';
import MultiFecha from './MultiFecha';
import { obtenerTextoDesdeEnum } from 'utils/utilities';
import { DiasSemana } from 'utils/models/types';
import { NavLink } from 'react-router-dom';

interface IPropsFormulario {
  campos: IFormato<IFormulario>[];
  titulo?: string;
  anchoMax?: number;
  mensaje?: string;
  icono?: string;
  iconoBoton: React.ComponentType<SvgIconProps>;
  variante?: 'claro' | 'oscuro';
  textoBoton?: string;
  colorBoton?: string;
  datos?: ISelectOptions[];
  botonSecundario?: string;
  onSecondaryClick?: any;
  onSubmit?: (data: IFormulario) => any;
  onVolver?: () => void;
  tituloMensajeResp?: string;
  mensajeExito: string;
  mensajeAlerta?: string;
  mensajeError?: string | null;
  loading?: boolean;
  handleFileUpload?: any;
  tituloPequenio?: boolean;
}

const Formulario = ({
  campos,
  titulo,
  anchoMax = 600,
  mensaje,
  variante = 'claro',
  icono,
  iconoBoton: IconoBoton,
  textoBoton = 'Enviar',
  colorBoton = theme.palette.secondary.main,
  datos,
  onSecondaryClick,
  botonSecundario = '',
  onSubmit,
  tituloMensajeResp,
  mensajeExito,
  mensajeAlerta,
  mensajeError,
  loading,
  onVolver,
  handleFileUpload,
  tituloPequenio,
}: IPropsFormulario) => {
  const {
    register,
    handleSubmit,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<IFormulario>();

  const [selectName, setSelectName] = useState<string[]>([]);

  const [dates, setDates] = useState<(Date | null)[]>([null]);

  const watchEsAlumno = watch('esAlumno', false);
  const watchEsSocio = watch('esSocio', false);
  const watchDias = watch('Dias', []);

  const [showPassword, setShowPassword] = useState(false);
  const [focus, setFocus] = useState('');

  const watchEmail = watch('email');
  const watchConfirmEmail = watch('confirmEmail');
  const watchPass = watch('password');
  const watchConfirmPass = watch('ConfirmPass');
  const watchTelefono = watch('telefono');
  const watchFunciones = watch('FechasObras');

  useEffect(() => {
    if (watchConfirmEmail) {
      if (watchEmail !== watchConfirmEmail) {
        setError('confirmEmail', {
          type: 'manual',
          message: 'Los emails deben coincidir',
        });
      } else {
        clearErrors('confirmEmail');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchEmail, watchConfirmEmail]);

  useEffect(() => {
    if (watchFunciones) {
      clearErrors('FechasObras');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchFunciones]);

  useEffect(() => {
    if (watchConfirmPass) {
      if (watchPass !== watchConfirmPass) {
        setError('ConfirmPass', {
          type: 'manual',
          message: 'Las contraseñas no coinciden',
        });
      } else {
        clearErrors('ConfirmPass');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchPass, watchConfirmPass]);

  useEffect(() => {
    const email = campos.find(c => c.name === 'email');
    if (email?.pattern && !email?.pattern.test(watchEmail) && focus === 'email') {
      setError('email', {
        type: 'manual',
        message: 'El email no sigue el formato válido',
      });
    } else {
      clearErrors('email');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchEmail]);

  useEffect(() => {
    const telefono = campos.find(c => c.name === 'telefono');
    if (telefono?.pattern && !telefono?.pattern.test(watchTelefono) && focus === 'telefono') {
      setError('telefono', {
        type: 'manual',
        message: 'El teléfono no sigue el formato válido',
      });
    } else {
      clearErrors('telefono');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchTelefono]);

  useEffect(() => {
    const pass = campos.find(c => c.name === 'password');

    if (pass?.pattern && !pass?.pattern.test(watchPass) && focus === 'password') {
      setError('password', {
        type: 'manual',
        message: 'El password no sigue el formato válido',
      });
    } else {
      clearErrors('password');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchPass]);

  useEffect(() => {
    const desplegablesCondicionales: string[] = [];
    if (watchEsAlumno) {
      desplegablesCondicionales.push('talleres');
    }
    if (watchEsSocio) {
      desplegablesCondicionales.push('tipoSocio');
    }
    setSelectName(desplegablesCondicionales);
  }, [watchEsAlumno, watchEsSocio]);

  const handleDateChange = (index: number, newValue: Date | null) => {
    const newDates = [...dates];
    newDates[index] = newValue;
    setDates(newDates);
  };

  const handleAddDateTime = () => {
    setDates([...dates, null]);
  };

  const handleRemoveDateTime = (index: number) => {
    setDates(dates.filter((_, i) => i !== index));
  };

  const HorarioSegunDia = ({ dia, index }: { dia: string; index: number }) => {
    const diaTexto = obtenerTextoDesdeEnum(DiasSemana, Number(dia));
    return (
      <Box key={dia} display="flex" gap="10px" marginBottom="15px">
        <TextField
          {...register(`Horarios.${index}.HorarioIni`, { required: `El horario de inicio es obligatorio.` })}
          label={`Horario de inicio ${diaTexto}`}
          type="time"
          InputLabelProps={{
            shrink: true,
          }}
          inputProps={{
            step: 900, // 15 min
          }}
          sx={{ width: '50%' }}
          error={!!errors.Horarios?.[index]?.HorarioIni}
          helperText={errors.Horarios?.[index]?.HorarioIni?.message}
          variant="filled"
        />
        <TextField
          {...register(`Horarios.${index}.HorarioFin`, { required: `El horario de fin es obligatorio.` })}
          label={`Horario de finalización ${diaTexto}`}
          type="time"
          InputLabelProps={{
            shrink: true,
          }}
          inputProps={{
            step: 900, // 15 min
          }}
          sx={{ width: '50%' }}
          error={!!errors.Horarios?.[index]?.HorarioFin}
          helperText={errors.Horarios?.[index]?.HorarioFin?.message}
          variant="filled"
        />
      </Box>
    );
  };

  const versionOscuro = variante === 'oscuro'; // Versión oscura solamente se utiliza en el footer para el boletín de novedades

  return (
    <ThemeProvider theme={versionOscuro ? darkTheme : theme}>
      {!!mensajeError && <Alerta mensaje={mensajeError} abierto={!!mensajeError} severity="error" />}
      {mensajeExito && onVolver ? (
        <PantallaExito
          titulo={tituloMensajeResp}
          mensaje={mensajeExito}
          subMensaje={mensajeAlerta}
          onVolver={onVolver}
        />
      ) : !!campos ? (
        <Box
          component="form"
          onSubmit={onSubmit && handleSubmit(onSubmit)}
          noValidate
          maxWidth={`${anchoMax}px!important`}
          width={versionOscuro ? '100%' : 'unset'}
          mx={versionOscuro ? '' : 'auto'}
          pt={titulo ? 4 : ''}
          pb={2}
        >
          <Box display="flex" justifyContent={icono ? 'space-around' : 'left'} gap="12px">
            <Typography
              variant={versionOscuro ? 'h5' : 'h3'}
              fontSize={versionOscuro ? '' : tituloPequenio ? '27px' : '40px'}
              color={versionOscuro ? 'white' : theme.palette.primary.main}
              textAlign="left"
              mb={2}
            >
              {titulo}
            </Typography>
            {icono && <Icono nombre={icono} color={variante === 'claro' ? 'black' : 'white'} />}
          </Box>

          {campos.map(
            (
              {
                name,
                label,
                inputType = 'text',
                enlace,
                halfWidth = '',
                required = true,
                placeholder = '',
                defaultValue = '',
                disabled = false,
                pattern,
                tooltip,
                adornment,
                subtituloSeccion,
              },
              index
            ) => {
              const mensajeValidacion = !disabled && `El campo ${label.toLowerCase()} es obligatorio`;
              const multipleSelect = name === 'talleres' || name === 'Dias';
              const shouldRenderSelect = selectName.includes(name) || (name !== 'talleres' && name !== 'tipoSocio');
              const opcionesSelect = datos?.filter(({ entidad }) => entidad === name) ?? [];
              const mensajeSinOpciones = name === 'talleres' ? 'No hay talleres registrados.' : 'No hay registros';

              return (
                <React.Fragment key={name + index}>
                  {subtituloSeccion && !versionOscuro && (
                    <>
                      <Divider sx={{ mt: 4 }} />
                      <Typography
                        variant="h6"
                        fontWeight={600}
                        fontSize="1rem"
                        textAlign="left"
                        mb={2}
                        pt={2}
                        color={theme.palette.secondary.dark}
                      >
                        {subtituloSeccion}
                      </Typography>
                    </>
                  )}
                  <FormControl
                    key={`${name}-${label}`}
                    margin="dense"
                    error={!!errors[name]}
                    sx={{
                      boxSizing: 'border-box',
                      width: !!halfWidth ? '50%' : '100%',
                      alignItems: inputType === 'checkbox' ? 'center' : '',
                      pr: inputType !== 'date' ? (halfWidth === 'left' ? '5px' : '') : '',
                      pl: halfWidth === 'right' ? '5px' : '',
                      mt: inputType === 'checkbox' ? '20px' : '',
                    }}
                  >
                    {shouldRenderSelect && inputType === 'select' && datos ? (
                      opcionesSelect.length > 0 ? (
                        <>
                          <InputLabel shrink={true} size="small">
                            {label}
                          </InputLabel>
                          <Select
                            {...register(name, { required: required && mensajeValidacion })}
                            label={label}
                            required={required}
                            name={name}
                            className={variante === 'oscuro' ? 'variante-oscura' : ''}
                            defaultValue={
                              multipleSelect
                                ? Array.isArray(defaultValue)
                                  ? defaultValue
                                  : []
                                : defaultValue
                                  ? defaultValue
                                  : ''
                            }
                            multiple={multipleSelect}
                            variant="filled"
                          >
                            {opcionesSelect.map(({ entidad, nombre, id, disabled }) => (
                              <MenuItem key={`${entidad}-${id}`} value={id} disabled={disabled}>
                                {nombre}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors[name] && (
                            <Typography fontSize={12} color="error" textAlign="left">
                              {errors[name]?.message}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Typography variant="h6" fontSize={16}>
                          {mensajeSinOpciones}
                        </Typography>
                      )
                    ) : inputType === 'checkbox' ? (
                      <>
                        <FormControlLabel
                          className="checkboxLabel"
                          sx={{ textAlign: 'left' }}
                          control={
                            <Checkbox
                              {...register(name, { required: required && mensajeValidacion })}
                              size="small"
                              name={name}
                              required={required}
                              defaultChecked={!!defaultValue}
                              className="checkbox"
                              disabled={disabled}
                            />
                          }
                          label={
                            enlace ? (
                              <Link to={enlace} component={NavLink} underline="none">
                                {label}
                              </Link>
                            ) : (
                              label
                            )
                          }
                        />
                        {errors[name] && <FormHelperText error>{errors[name]?.message}</FormHelperText>}
                      </>
                    ) : (
                      inputType !== 'select' &&
                      inputType !== 'datepicker' &&
                      inputType !== 'dropzone' &&
                      inputType !== 'time' && (
                        <>
                          <TextField
                            {...register(name, {
                              required: required && !disabled ? mensajeValidacion : false,
                              minLength: {
                                value: inputType === 'text' ? 3 : 1, // Solo aplica la validación de 3 caracteres a campos de texto
                                message: inputType === 'text' ? 'El campo debe tener al menos 3 caracteres' : '',
                              },

                              ...(pattern
                                ? { pattern: { value: pattern, message: tooltip ?? '' } }
                                : {}),
                            })}
                            name={name}
                            label={inputType === 'textarea' && mensaje ? mensaje : label}
                            variant="filled"
                            type={inputType === 'password' && showPassword ? 'text' : inputType}
                            required={required}
                            multiline={inputType === 'textarea'}
                            rows={inputType === 'textarea' && name === 'Body' ? 10 : inputType === 'textarea' ? 5 : 1}
                            error={!!errors[name]}
                            helperText={errors[name]?.message}
                            onFocus={() => setFocus(name)}
                            placeholder={placeholder}
                            defaultValue={defaultValue}
                            InputLabelProps={{
                              style: {
                                color: variante === 'oscuro' ? 'white' : '',
                              },
                              shrink: true,
                            }}
                            InputProps={{
                              readOnly: (inputType === 'email' && !!defaultValue) || disabled === true,
                              startAdornment:
                                inputType === 'password' ? (
                                  <InputAdornment position="start">
                                    <IconButton
                                      onClick={() => setShowPassword(!showPassword)}
                                      sx={{ padding: '0' }}
                                      tabIndex={-1}
                                    >
                                      {showPassword ? (
                                        <VisibilityOff sx={{ fontSize: '16px' }} />
                                      ) : (
                                        <Visibility sx={{ fontSize: '16px' }} />
                                      )}
                                    </IconButton>
                                  </InputAdornment>
                                ) : adornment === 'tel' ? (
                                  <InputAdornment position="start">
                                    <PhoneIcon sx={{ fontSize: '16px' }} />
                                  </InputAdornment>
                                ) : adornment === 'email' ? (
                                  <InputAdornment position="start">
                                    <EmailIcon sx={{ fontSize: '16px' }} />
                                  </InputAdornment>
                                ) : adornment === 'account' ? (
                                  <InputAdornment position="start">
                                    <PersonIcon sx={{ fontSize: '16px' }} />
                                  </InputAdornment>
                                ) : (
                                  <InputAdornment position="start" className="inputAdornment">
                                    {adornment}
                                  </InputAdornment>
                                ),
                            }}
                            sx={label === 'Fecha de inicio' ? { paddingRight: '5px' } : {}}
                          />

                          {tooltip && (
                            <Tooltip title={tooltip} arrow placement="right" sx={{ position: 'relative' }}>
                              <InfoOutlinedIcon
                                sx={{ position: 'absolute', right: '10px', top: '35%', fontSize: '14px' }}
                                className="tooltipIcon"
                              />
                            </Tooltip>
                          )}
                        </>
                      )
                    )}

                    {inputType === 'datepicker' && (
                      <MultiFecha
                        fechas={dates}
                        handleDateChange={handleDateChange}
                        handleAddDateTime={handleAddDateTime}
                        handleRemoveDateTime={handleRemoveDateTime}
                        errors={errors.FechasObras}
                        register={register}
                        label={label}
                        name="FechasObras"
                        mensajeValidacion={mensajeValidacion}
                      />
                    )}

                    {inputType === 'dropzone' && (
                      <Box
                        height="56px"
                        display="flex"
                        boxSizing="border-box"
                        overflow="auto"
                        pl={3}
                        pt={3}
                        sx={{
                          backgroundColor: theme.palette.secondary.contrastText,
                          borderBottom: '1px solid rgba(0, 0, 0, 0.5)',
                        }}
                      >
                        <InputLabel shrink={true} size="small" sx={{ position: 'absolute', top: '15px' }}>
                          Subir fotos
                        </InputLabel>
                        <input
                          type="file"
                          multiple
                          accept="image/*"
                          onChange={handleFileUpload}
                          style={{ fontSize: 16, fontWeight: 600 }}
                        />
                      </Box>
                    )}
                  </FormControl>
                </React.Fragment>
              );
            }
          )}

          {Array.isArray(watchDias) &&
            watchDias.length > 0 &&
            watchDias.map((dia: React.Key, index: number) => (
              <HorarioSegunDia key={dia} dia={dia.toString()} index={index} />
            ))}

          <Box display="flex" flexDirection={botonSecundario ? 'row' : 'column'} justifyContent="center" gap={1}>
            {botonSecundario && (
              <Button
                type="button"
                onClick={onSecondaryClick}
                variant="outlined"
                className="loadingButton"
                sx={{
                  my: !versionOscuro ? 4 : 0,
                  mt: versionOscuro ? 2 : 4,
                  backgroundColor: theme.palette.primary.main,
                  width: '100%',
                  color: 'white',
                  '&:hover': {
                    color: theme.palette.primary.main,
                  },
                }}
              >
                {botonSecundario}
              </Button>
            )}
            <LoadingButton
              type="submit"
              size={versionOscuro ? 'medium' : 'large'}
              variant="contained"
              sx={{ my: !versionOscuro ? 4 : 0, mt: versionOscuro ? 2 : 4, backgroundColor: colorBoton, width: '100%' }}
              fullWidth={!botonSecundario}
              loading={loading}
              className="loadingButton"
              loadingPosition="start"
              startIcon={<IconoBoton />}
            >
              {textoBoton}
            </LoadingButton>
          </Box>
        </Box>
      ) : (
        <Typography>No se encontraron datos.</Typography>
      )}
    </ThemeProvider>
  );
};

export default Formulario;
